import React, { useState, useCallback } from 'react';
import {
  Download,
  Loader2,
  RefreshCw,
  Clock,
  BarChart3,
  Users,
  GraduationCap,
  Activity,
  Target,
  Building2,
  Award,
  Zap,
  CalendarDays,
  ArrowUpRight,
  BookOpen,
  AlertCircle,
  CheckCircle,
  X,
} from 'lucide-react';
import type { DashboardKpiStats, CourseCompletionEntry, DepartmentPerformance, UserReportEntry, LearningEngagement } from '@/services/PagesServices/AnalyticsService';
import { toast } from 'react-toastify';
import { LoadingSpinner } from '@/Components/common';
import { useAnalyticsData } from '../hooks/useAnalyticsData';
import { computeInsights, safeNum, ComputedInsights } from '../utils';
import { exportAnalyticsPDF } from '@/utils/analyticsPdfExport';
import ReportSchedulerModal from '@/Components/ReportScheduler/ReportSchedulerModal';
import OverviewTab from './OverviewTab';
import UsersTab from './UsersTab';
import CoursesTab from './CoursesTab';
import EngagementTab from './EngagementTab';
import '../../../pages/Admin/Analytics/Analytics.css';

type TabKey = 'overview' | 'users' | 'courses' | 'engagement';

const TABS: { key: TabKey; label: string; icon: React.ReactNode }[] = [
  { key: 'overview', label: 'Overview', icon: <BarChart3 size={16} /> },
  { key: 'users', label: 'Users', icon: <Users size={16} /> },
  { key: 'courses', label: 'Courses', icon: <GraduationCap size={16} /> },
  { key: 'engagement', label: 'Engagement', icon: <Activity size={16} /> },
];

const AnalyticsPage: React.FC = () => {
  const { data, isLoading, refetch, isRefetching } = useAnalyticsData();
  const [activeTab, setActiveTab] = useState<TabKey>('overview');
  const [exporting, setExporting] = useState(false);
  const [showSchedulerModal, setShowSchedulerModal] = useState(false);
  const [insightModal, setInsightModal] = useState<'completion' | 'department' | 'certifications' | 'enrollment' | null>(null);

  const handleExportPDF = useCallback(async () => {
    if (!data) return;
    setExporting(true);
    try {
      await exportAnalyticsPDF(data);
      toast.success('Analytics report downloaded successfully');
    } catch {
      toast.error('Failed to generate PDF report');
    } finally {
      setExporting(false);
    }
  }, [data]);

  // Derived data
  const stats = data?.dashboardStats ?? [];
  const engagement = data?.learningEngagement ?? null;
  const deptPerformance = data?.combinedAnalytics?.departmentPerformance ?? [];
  const courseCompletion = data?.courseCompletionData ?? [];
  const userReport = data?.userReportData ?? [];

  const insights = React.useMemo(
    () => computeInsights(courseCompletion, deptPerformance, userReport, engagement),
    [courseCompletion, deptPerformance, userReport, engagement]
  );

  if (isLoading) {
    return (
      <div className="analytics-page">
        <div className="analytics-loading">
          <LoadingSpinner size="lg" />
          <p>Loading analytics data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="analytics-page">
      {/* Header */}
      <div className="analytics-page-header">
        <div>
          <h1>Analytics</h1>
          <p>Comprehensive insights and data across the learning platform.</p>
        </div>
        <div className="analytics-header-actions">
          <button className="analytics-refresh-btn" onClick={() => refetch()} disabled={isRefetching}>
            <RefreshCw size={16} className={isRefetching ? 'analytics-export-spinner' : ''} />
          </button>
          <button className="analytics-export-btn" onClick={handleExportPDF} disabled={exporting || !data}>
            {exporting ? (
              <><Loader2 size={16} className="analytics-export-spinner" /> Exporting...</>
            ) : (
              <><Download size={16} /> Export PDF</>
            )}
          </button>
          <button className="analytics-schedule-btn" onClick={() => setShowSchedulerModal(true)}>
            <CalendarDays size={16} /> Schedule Report
          </button>
        </div>
      </div>

      {/* KPI Stats */}
      {stats.length > 0 && <KpiStatsGrid stats={stats} />}

      {/* Quick Insights Bar */}
      <div className="analytics-insights-bar">
        <div className="analytics-insight-chip analytics-insight-clickable" onClick={() => setInsightModal('completion')}>
          <Target size={16} /><span>Avg Completion Rate</span><strong>{insights.avgCompletionRate}%</strong>
        </div>
        <div className="analytics-insight-chip analytics-insight-clickable" onClick={() => setInsightModal('department')}>
          <Building2 size={16} /><span>Top Department</span><strong>{insights.topDept?.department ?? 'N/A'}</strong>
        </div>
        <div className="analytics-insight-chip analytics-insight-clickable" onClick={() => setInsightModal('certifications')}>
          <Award size={16} /><span>Total Certifications</span><strong>{insights.totalCerts}</strong>
        </div>
        <div className="analytics-insight-chip analytics-insight-clickable" onClick={() => setInsightModal('enrollment')}>
          <Zap size={16} /><span>Enrollment Rate</span><strong>{insights.enrollmentRate}%</strong>
        </div>
      </div>

      {/* Tabs */}
      <div className="analytics-tabs">
        {TABS.map(tab => (
          <button
            key={tab.key}
            className={`analytics-tab ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && data && (
        <OverviewTab
          engagement={data.learningEngagement ?? null}
          learningProgress={data.learningProgress ?? null}
          topCourses={data.topCourses ?? []}
          leaderboard={data.combinedAnalytics?.leaderboard ?? []}
          deptPerformance={deptPerformance}
          completionTrends={data.combinedAnalytics?.completionTrends ?? []}
          categoryBreakdown={data.categoryBreakdown ?? []}
          certDistribution={data.combinedAnalytics?.certificationDistribution ?? []}
          skillsAssessment={data.combinedAnalytics?.skillsAssessment ?? []}
          roleDistribution={data.roleDistribution ?? []}
          learningHours={data.learningHours ?? []}
          progressDistribution={data.progressDistribution ?? null}
          courseCompletion={courseCompletion}
          userReport={userReport}
        />
      )}
      {activeTab === 'users' && data && (
        <UsersTab
          userReport={userReport}
          deptPerformance={deptPerformance}
          userGrowth={data.userGrowth ?? []}
        />
      )}
      {activeTab === 'courses' && data && (
        <CoursesTab
          courseCompletion={courseCompletion}
          categoryBreakdown={data.categoryBreakdown ?? []}
          topCourses={data.topCourses ?? []}
          progressDistribution={data.progressDistribution ?? null}
        />
      )}
      {activeTab === 'engagement' && data && (
        <EngagementTab
          engagement={data.learningEngagement ?? null}
          loginActivity={data.loginActivity ?? []}
          enrollmentTimeline={data.enrollmentTimeline ?? []}
          learningHours={data.learningHours ?? []}
          activityHeatmap={data.activityHeatmap ?? []}
          insights={insights}
        />
      )}

      <ReportSchedulerModal isOpen={showSchedulerModal} onClose={() => setShowSchedulerModal(false)} />

      {/* Insight Detail Modal */}
      {insightModal && (
        <InsightDetailModal
          type={insightModal}
          insights={insights}
          courseCompletion={courseCompletion}
          deptPerformance={deptPerformance}
          userReport={userReport}
          engagement={engagement}
          onClose={() => setInsightModal(null)}
        />
      )}
    </div>
  );
};

// --- KPI Stats sub-component ---

const STAT_CONFIG: Record<string, { bg: string; color: string; icon: React.ReactNode }> = {
  users: { bg: 'var(--color-light-orange-bg, #fff3e8)', color: 'var(--color-orange-icon, #ff8c42)', icon: <Users size={22} /> },
  book: { bg: '#eef2ff', color: '#6366f1', icon: <BookOpen size={22} /> },
  alert: { bg: '#fef3c7', color: '#f59e0b', icon: <AlertCircle size={22} /> },
  check: { bg: '#dcfce7', color: '#16a34a', icon: <CheckCircle size={22} /> },
};

const DEFAULT_STAT = { bg: '#f0f9ff', color: '#0ea5e9', icon: <BarChart3 size={22} /> };

const KpiStatsGrid: React.FC<{ stats: DashboardKpiStats[] }> = ({ stats }) => (
  <div className="analytics-stats-grid">
    {stats.map((stat, i) => {
      const config = STAT_CONFIG[stat.iconType] ?? DEFAULT_STAT;
      return (
        <div key={i} className="analytics-stat-card">
          <div className="analytics-stat-header">
            <div>
              <div className="analytics-stat-title">{stat.title}</div>
              <div className="analytics-stat-value">{stat.value.toLocaleString()}</div>
            </div>
            <div className="analytics-stat-icon" style={{ background: config.bg, color: config.color }}>
              {config.icon}
            </div>
          </div>
          <div className="analytics-stat-increase">
            <ArrowUpRight size={14} /> + {stat.increase} from last week
          </div>
        </div>
      );
    })}
  </div>
);

// --- Insight Detail Modal ---

const InsightDetailModal: React.FC<{
  type: 'completion' | 'department' | 'certifications' | 'enrollment';
  insights: ComputedInsights;
  courseCompletion: CourseCompletionEntry[];
  deptPerformance: DepartmentPerformance[];
  userReport: UserReportEntry[];
  engagement: LearningEngagement | null;
  onClose: () => void;
}> = ({ type, insights, courseCompletion, deptPerformance, userReport, engagement, onClose }) => {

  const titles: Record<string, string> = {
    completion: 'Avg Completion Rate',
    department: 'Department Performance',
    certifications: 'Certifications Breakdown',
    enrollment: 'Enrollment Rate Details',
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }} onClick={onClose}>
      <div style={{
        backgroundColor: 'white', borderRadius: '12px', padding: '24px',
        width: '100%', maxWidth: '620px', maxHeight: '80vh', overflow: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
      }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>{titles[type]}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
            <X size={20} color="#6b7280" />
          </button>
        </div>

        {type === 'completion' && (
          <div>
            <div style={{ fontSize: '28px', fontWeight: 700, color: '#1f2937', marginBottom: '16px' }}>
              {insights.avgCompletionRate}%
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ textAlign: 'left', padding: '8px', color: '#6b7280' }}>Course</th>
                  <th style={{ textAlign: 'left', padding: '8px', color: '#6b7280' }}>Category</th>
                  <th style={{ textAlign: 'right', padding: '8px', color: '#6b7280' }}>Enrolled</th>
                  <th style={{ textAlign: 'right', padding: '8px', color: '#6b7280' }}>Completed</th>
                  <th style={{ textAlign: 'right', padding: '8px', color: '#6b7280' }}>Rate</th>
                </tr>
              </thead>
              <tbody>
                {courseCompletion.slice(0, 15).map((c, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '8px', maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.title}</td>
                    <td style={{ padding: '8px', color: '#6b7280' }}>{c.category}</td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>{c.totalEnrollments}</td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>{c.completed}</td>
                    <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: safeNum(c.completionRate) >= 50 ? '#059669' : '#dc2626' }}>
                      {safeNum(c.completionRate).toFixed(0)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {courseCompletion.length === 0 && <p style={{ color: '#9ca3af', textAlign: 'center' }}>No course data available</p>}
          </div>
        )}

        {type === 'department' && (
          <div>
            <div style={{ fontSize: '16px', fontWeight: 600, color: '#1f2937', marginBottom: '16px' }}>
              Top: {insights.topDept?.department ?? 'N/A'} ({safeNum(insights.topDept?.completionPercentage).toFixed(0)}% completion)
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ textAlign: 'left', padding: '8px', color: '#6b7280' }}>Department</th>
                  <th style={{ textAlign: 'right', padding: '8px', color: '#6b7280' }}>Users</th>
                  <th style={{ textAlign: 'right', padding: '8px', color: '#6b7280' }}>Enrolled</th>
                  <th style={{ textAlign: 'right', padding: '8px', color: '#6b7280' }}>Completion</th>
                </tr>
              </thead>
              <tbody>
                {[...deptPerformance].sort((a, b) => safeNum(b.completionPercentage) - safeNum(a.completionPercentage)).map((d, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f3f4f6', backgroundColor: i === 0 ? '#f0fdf4' : 'transparent' }}>
                    <td style={{ padding: '8px', fontWeight: i === 0 ? 600 : 400 }}>{d.department}</td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>{d.totalUsers}</td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>{d.usersWithEnrollments}</td>
                    <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>
                      <span style={{
                        padding: '2px 8px', borderRadius: '10px', fontSize: '12px',
                        backgroundColor: safeNum(d.completionPercentage) >= 50 ? '#dcfce7' : '#fee2e2',
                        color: safeNum(d.completionPercentage) >= 50 ? '#166534' : '#991b1b',
                      }}>
                        {safeNum(d.completionPercentage).toFixed(0)}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {deptPerformance.length === 0 && <p style={{ color: '#9ca3af', textAlign: 'center' }}>No department data available</p>}
          </div>
        )}

        {type === 'certifications' && (
          <div>
            <div style={{ fontSize: '28px', fontWeight: 700, color: '#1f2937', marginBottom: '16px' }}>
              {insights.totalCerts} total
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ textAlign: 'left', padding: '8px', color: '#6b7280' }}>User</th>
                  <th style={{ textAlign: 'left', padding: '8px', color: '#6b7280' }}>Department</th>
                  <th style={{ textAlign: 'right', padding: '8px', color: '#6b7280' }}>Courses</th>
                  <th style={{ textAlign: 'right', padding: '8px', color: '#6b7280' }}>Certificates</th>
                </tr>
              </thead>
              <tbody>
                {[...userReport].filter(u => safeNum(u.certificatesEarned) > 0)
                  .sort((a, b) => safeNum(b.certificatesEarned) - safeNum(a.certificatesEarned))
                  .slice(0, 15)
                  .map((u, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '8px' }}>{u.fullName}</td>
                      <td style={{ padding: '8px', color: '#6b7280' }}>{u.department || '-'}</td>
                      <td style={{ padding: '8px', textAlign: 'right' }}>{u.coursesCompleted}</td>
                      <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>
                        <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '12px', backgroundColor: '#fef3c7', color: '#92400e' }}>
                          {safeNum(u.certificatesEarned)}
                        </span>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
            {userReport.filter(u => safeNum(u.certificatesEarned) > 0).length === 0 && (
              <p style={{ color: '#9ca3af', textAlign: 'center' }}>No certifications earned yet</p>
            )}
          </div>
        )}

        {type === 'enrollment' && (
          <div>
            <div style={{ fontSize: '28px', fontWeight: 700, color: '#1f2937', marginBottom: '4px' }}>
              {insights.enrollmentRate}%
            </div>
            <p style={{ color: '#6b7280', fontSize: '13px', marginBottom: '16px' }}>
              {safeNum(engagement?.usersWithEnrollments)} of {safeNum(engagement?.totalActiveUsers)} active users have enrolled in courses
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              <div style={{ padding: '14px', backgroundColor: '#f0f9ff', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '22px', fontWeight: 700, color: '#0369a1' }}>{safeNum(engagement?.totalActiveUsers)}</div>
                <div style={{ fontSize: '12px', color: '#6b7280' }}>Total Active Users</div>
              </div>
              <div style={{ padding: '14px', backgroundColor: '#f0fdf4', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '22px', fontWeight: 700, color: '#059669' }}>{safeNum(engagement?.usersWithEnrollments)}</div>
                <div style={{ fontSize: '12px', color: '#6b7280' }}>Users Enrolled</div>
              </div>
              <div style={{ padding: '14px', backgroundColor: '#fef3c7', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '22px', fontWeight: 700, color: '#d97706' }}>{safeNum(engagement?.totalEnrollments)}</div>
                <div style={{ fontSize: '12px', color: '#6b7280' }}>Total Enrollments</div>
              </div>
              <div style={{ padding: '14px', backgroundColor: '#eff6ff', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '22px', fontWeight: 700, color: '#2563eb' }}>{safeNum(engagement?.activeUsersLast7Days)}</div>
                <div style={{ fontSize: '12px', color: '#6b7280' }}>Active (Last 7 Days)</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalyticsPage;
